<!-- eslint-disable vue/no-v-html -->
<template>
    <onboarding-quicktrial-layout :isDesktop="isDesktop">
        <template #banner>
            <div class="onboarding-quicktrial__banner">
                <div class="onboarding-quicktrial__banner-header">
                    <p class="onboarding-quicktrial__banner-header-title">
                        {{ $t.__('common.labels.15_days_free_trial') }} 🚀
                    </p>
                </div>
                <p class="onboarding-quicktrial__banner-baseline">
                    {{ $t.__('common.labels.without_commitment') }} -
                    {{ $t.__('common.labels.no_credit_card_required') }}
                </p>
                <p class="onboarding-quicktrial__banner-details" v-html="translations.details" />
                <div class="onboarding-quicktrial__banner-illustration-container">
                    <img
                        :src="banner"
                        class="onboarding-quicktrial__banner-illustration"
                        :alt="$t.__('common.labels.freely_test_all_features_alt')"
                    />
                </div>
                <p
                    class="onboarding-quicktrial__banner-subtitle"
                    v-html="$t.__('common.labels.companies_trust_sellsy', { br: '<br />' })"
                />
                <customer-logo class="onboarding-quicktrial__banner-customer-logo" />
            </div>
        </template>
        <router-view></router-view>
    </onboarding-quicktrial-layout>
</template>

<script>
import OnboardingQuicktrialLayout from './OnboardingQuicktrialLayout.vue';
import { createNamespacedHelpers } from 'vuex';
import { isDesktop } from 'utils/global';
import Store from 'Services/Store';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import LocalStorage from 'Core/commons/LocalStorage';
import CustomerLogo from 'vcomponents/icons/CustomerLogo.vue';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');

/**
 * user don't must be logged
 */
const guardIslogged = () => {
    const user = Store.get('user');
    return user.id;
};

/**
 * query param lang must be defined
 */
const guardIsNotDefineLang = (to) => !to.query.lang;

/**
 * query param lang must be available
 */
const guardIsNotAvailableLang = (to) => {
    const availableLang = Store.get('system').availableLanguages.map(({ id }) => id);
    return !availableLang.includes(to.query.lang);
};

/**
 * Query params are set by Google Auth
 * Named route condition when route is already on GoogleComponent
 */
const guardPassToGoogleSignup = (to) =>
    !!to.query.email && !!to.query.id && to.name !== 'onboarding.quicktrial.google';

const startSpanBlue = '<span class="onboarding-quicktrial__banner-details--blue">';
const startSpanAi = '<span class="onboarding-quicktrial__banner-details--ai">';
const endSpan = '</span>';

export default {
    components: {
        OnboardingQuicktrialLayout,
        CustomerLogo,
    },
    beforeRouteEnter(to, from, next) {
        if (guardIslogged()) {
            window.location.href = '/';
        } else if (guardIsNotDefineLang(to)) {
            const langStorage = new LocalStorage('onboardingTrial', 'lang').get()?.lang || 'fr';
            to.query.lang = langStorage;
            const queryStringParameters = Object.keys(to.query)
                .map((key) => key + '=' + to.query[key])
                .join('&');
            window.location.href = `${to.path}?${queryStringParameters}`;
        } else if (guardIsNotAvailableLang(to)) {
            to.query.lang = 'fr';
            next({ path: to.path, query: to.query });
        } else if (guardPassToGoogleSignup(to)) {
            next({ name: 'onboarding.quicktrial.google', query: to.query });
        } else {
            next();
        }
    },
    data() {
        return {
            isDesktop: isDesktop(),
            translations: {
                details: this.$t.__('common.labels.onboardingquicktrial_details', {
                    startSpanBlue: startSpanBlue,
                    startSpanAi: startSpanAi,
                    endSpan: endSpan,
                }),
            },
        };
    },
    computed: {
        ...mapStateSystem(['reCaptchav3', 'availableLanguages']),
        banner() {
            return this.$route.query.lang === 'fr'
                ? '/ressources/logged/img/onboarding/trial-illustration-fr.png'
                : '/ressources/logged/img/onboarding/trial-illustration-en.png';
        },
    },
    created() {
        if (this.reCaptchav3.isEnabled) {
            window.sellsyapp.use(VueReCaptcha, { siteKey: this.reCaptchav3.siteKey });
        }

        window.addEventListener('resize', () => {
            this.isDesktop = isDesktop();
        });
    },
};
</script>

<style lang="scss">
.onboarding-quicktrial {
    &__banner {
        margin: auto 0;
        text-align: center;
        height: calc(100vh - 64px);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        overflow: hidden;

        @media screen and (max-width: 1023px) {
            display: none;
        }

        &-header {
            position: relative;

            &-title {
                font-family: var(--font-families-stack-sans-text);
                font-weight: 500;
                line-height: var(--line-heights-30);
                font-size: var(--font-size-24);
                color: var(--colors-common-text-brand-secondary-blue);
                margin: 0;
            }
        }

        &-baseline {
            font-family: var(--font-families-stack-sans-text);
            font-weight: 500;
            line-height: var(--line-heights-40);
            font-size: var(--font-size-17);
            color: var(--colors-common-text-brand-secondary-blue);
            margin: 0;
        }

        &-details {
            font-family: var(--font-families-stack-sans-text);
            font-weight: 300;
            line-height: var(--line-heights-24);
            font-size: var(--font-size-17);
            color: var(--colors-common-text-brand-secondary-blue);
            margin: var(--spacing-3) 0 0;

            &--blue,
            &--ai {
                font-weight: 500;
            }

            &--blue {
                color: var(--colors-common-text-primary-default);
            }
            &--ai {
                color: #af6dd3;
            }
        }

        &-illustration {
            max-width: 400px;
            margin: 0 auto;
            max-height: 100%;
            width: auto;

            &-container {
                max-height: calc(100% - 180px);
                margin-top: var(--spacing-4);
            }
        }

        &-subtitle {
            font-family: var(--font-families-stack-sans-text);
            font-weight: 500;
            line-height: var(--line-heights-20);
            font-size: var(--font-size-17);
            color: var(--colors-common-text-brand-secondary-blue);
            margin: var(--spacing-5) 0 0;
        }

        &-customer-logo {
            margin-top: var(--spacing-4);
        }
    }
}

@media screen and (max-height: 800px) {
    .onboarding-quicktrial {
        &__banner {
            &-header {
                &-title {
                    font-size: var(--font-size-20);
                }
            }

            &-baseline {
                font-size: var(--font-size-14);
            }

            &-details {
                font-size: var(--font-size-14);
            }

            &-illustration {
                max-width: 300px;
            }
        }
    }
}
</style>
